

const ALLOWED_IMAGE_MIME_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
]);

const NULLISH_STRINGS = new Set(["null", "undefined", "none", "n/a"]);

// Strict base64 character set — rejects anything containing characters
// outside A-Z, a-z, 0-9, +, /, = (padding). atob() alone is not a strict
// enough validator since it tolerates some malformed input.
const BASE64_PATTERN = /^[A-Za-z0-9+/]+={0,2}$/;

const isValidBase64 = (value: string): boolean => {
  if (!value || value.length % 4 !== 0) return false;
  if (!BASE64_PATTERN.test(value)) return false;
  try {
    atob(value);
    return true;
  } catch {
    return false;
  }
};

const detectImageMime = (base64: string): string | null => {
  const sig = base64.slice(0, 24);
  if (sig.startsWith("iVBORw0KGgo")) return "image/png";
  if (sig.startsWith("/9j/")) return "image/jpeg";
  if (sig.startsWith("R0lGOD")) return "image/gif";
  if (sig.startsWith("UklGR")) return "image/webp";
  return null;
};

/**
 * Centralized, allow-list-based sanitizer for any value that will be used
 * as an <img src>. Returns "" if the value is missing, malformed, or uses
 * a disallowed scheme/mime type — callers should fall back to a
 * placeholder in that case. This is the single source of truth: do not
 * duplicate this logic elsewhere.
 */
export const resolveImageSrc = (value?: string | null): string => {
  if (value == null) return "";

  const trimmed = value.trim();
  if (!trimmed) return "";
  if (NULLISH_STRINGS.has(trimmed.toLowerCase())) return "";

  // --- Case 1: full URL ---
  // Only allow https (and http for local/dev if you truly need it — drop
  // it if not). Explicitly reject javascript:, vbscript:, file:, etc.
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      if (url.protocol !== "https:" && url.protocol !== "http:") return "";
      return url.toString();
    } catch {
      return "";
    }
  }

  // --- Case 2: already a data URI ---
  if (/^data:/i.test(trimmed)) {
    const match = trimmed.match(/^data:([^;]+);base64,([\s\S]*)$/i);
    if (!match) return ""; // reject non-base64 data URIs (e.g. data:text/html,...)
    const [, mime, payload] = match;
    if (!ALLOWED_IMAGE_MIME_TYPES.has(mime.toLowerCase())) return "";
    if (!isValidBase64(payload.replace(/\s/g, ""))) return "";
    return trimmed;
  }

  // --- Case 3: raw base64 ---
  const cleaned = trimmed.replace(/\s/g, "");
  if (!isValidBase64(cleaned)) return "";
  const mime = detectImageMime(cleaned);
  if (!mime) return ""; // unrecognized signature — don't guess, reject
  return `data:${mime};base64,${cleaned}`;
};